import { ChannelType, isPlugChannelType, type PlugChannelType } from './ChannelType';

/** Socket identity is persisted on the shared consumer channel. */
export type ConsumerDeviceKind = 'light' | 'socket' | 'fixedPower' | 'camera' | 'blind' | 'jalousie' | 'heating' | 'contact' | 'doorLock' | 'other';
export type ConsumerPlugType = PlugChannelType;

export interface ConsumerDeviceSource {
  channelType: string;
  prefixType?: string;
  image?: string;
  c1?: {
    definition?: {type: string};
    data: {details?: {plugType?: string}};
  };
}
export interface ConsumerDeviceClassification {
  kind: ConsumerDeviceKind;
  plugType?: ConsumerPlugType;
}

const definitionKinds: Record<string, ConsumerDeviceKind> = {
  light: 'light', plug: 'socket', fixed_power: 'fixedPower', camera: 'camera',
  blind: 'blind', jalousie: 'jalousie', heating: 'heating', thermostat: 'heating',
  window_contact: 'contact', door_lock: 'doorLock',
};

/** C1 detail snapshots never override the owner's channel type. */
export function classifyConsumerDevice(source: ConsumerDeviceSource): ConsumerDeviceClassification {
  if (isPlugChannelType(source.channelType)) return {kind:'socket',plugType:source.channelType};
  const kind = definitionKinds[source.c1?.definition?.type ?? ''];
  if (kind && kind !== 'socket') return {kind};
  // LAN remains an outlet even when an old project also carries a generic socket prefix.
  if (source.channelType === 'Lan' || source.channelType === 'Lan2') {
    return {kind: 'socket', plugType: source.channelType === 'Lan2' ? ChannelType.plug_LAN2 : ChannelType.plug_LAN};
  }
  if (source.prefixType === 'PlugSwitchable' || source.channelType === 'PlugSwitchable') return {kind: 'socket', plugType: ChannelType.plug_Switch};
  if (source.prefixType === 'Plug' || source.channelType === 'Plug') return {kind: 'socket', plugType: ChannelType.plug_230V};
  // Match known assets only; a custom filename containing "plug" is not a device declaration.
  const asset = source.image?.split(/[?#]/)[0].split('/').pop()?.toLowerCase();
  if (asset === 'plugswitchable.png' || asset === 'plugswitchable_dali.png') return {kind: 'socket', plugType: ChannelType.plug_Switch};
  if (asset === 'plug.png' || asset === 'plug_dali.png') return {kind: 'socket', plugType: ChannelType.plug_230V};
  if (kind === 'socket') return {kind, plugType: source.channelType === 'Switch' ? ChannelType.plug_Switch : ChannelType.plug_230V};
  switch (source.channelType) {
    case 'Switch': case 'StaircaseLight': case 'Dimmer': case 'TuneableWhite': case 'Rgb': return {kind: 'light'};
    case 'suply230V': case 'suply400V': case 'suply12V': case 'suply24V': return {kind: 'fixedPower'};
    case 'Camera': return {kind: 'camera'};
    case 'Blind': return {kind: 'blind'};
    case 'Jalousie': return {kind: 'jalousie'};
    case 'Heating': case 'TemperatureSetpoint': case 'TemperatureMeasurement': case 'OperationMode': return {kind: 'heating'};
    case 'Lock': return {kind: 'contact'};
    case 'DoorLock': return {kind: 'doorLock'};
    default: return {kind: 'other'};
  }
}

/** Uses the same identity for automatic icons in the navigator and main application. */
export function consumerSymbolChannelType(source: ConsumerDeviceSource): string {
  const device = classifyConsumerDevice(source);
  if (device.kind !== 'socket') return source.channelType;
  return device.plugType ?? source.channelType;
}
